/**
 * Minimal markdown renderer.
 *
 * Deliberately tiny and dependency-free: the console renders repo markdown that
 * we control, and a full markdown library would be the largest thing in the
 * bundle. Handles headings, lists, code, blockquotes, tables, links, emphasis.
 *
 * Security: output is escaped first, so raw HTML in items cannot inject.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Inline formatting: code, bold, italic, links. */
function inline(s: string): string {
  let out = escapeHtml(s);
  // inline code first (so its contents are not further formatted)
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, c: string) => {
    codes.push(c);
    return `\u0000${codes.length - 1}\u0000`;
  });
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  out = out.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => `<code>${codes[Number(i)]}</code>`);
  return out;
}

export function renderMarkdown(md: string): string {
  // Normalise line endings: Windows files carry `\r\n`. A trailing `\r` breaks
  // the `$` anchors (JS `$` matches before `\n`, not before `\r`), which made
  // list-item regexes fail and the renderer loop forever. Strip `\r` first.
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const html: string[] = [];
  let i = 0;

  const flushList = (ordered: boolean) => {
    const items: string[] = [];
    const re = ordered ? /^(\s*)(\d+)\.\s+(.*)$/ : /^(\s*)([-*])\s+(.*)$/;
    const isItem = (l: string | undefined) => (l === undefined ? false : re.test(l));
    let start = 1;
    while (i < lines.length) {
      const m = re.exec(lines[i]);
      if (!m) break;
      if (items.length === 0 && ordered) start = Number(m[2]);
      const parts = [m[3]];
      i++;
      // Continuation lines (indented, not a new marker) belong to the item above.
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        /^\s{2,}/.test(lines[i]) &&
        !isItem(lines[i])
      ) {
        parts.push(lines[i].trim());
        i++;
      }
      items.push(`<li>${inline(parts.join(" "))}</li>`);
      // Loose lists: ONE blank line followed by another item keeps the same
      // list. Without this every item became its own <ol> and restarted at 1,
      // so a list rendered as "1. 1. 1. …".
      if (i < lines.length && lines[i].trim() === "" && isItem(lines[i + 1])) {
        i++;
      }
    }
    const startAttr = ordered && start !== 1 ? ` start="${start}"` : "";
    html.push(ordered ? `<ol${startAttr}>${items.join("")}</ol>` : `<ul>${items.join("")}</ul>`);
  };

  while (i < lines.length) {
    const line = lines[i];

    // fenced code
    if (/^\s*```/.test(line)) {
      const lang = line.replace(/^\s*```/, "").trim();
      i++;
      const buf: string[] = [];
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // closing fence
      html.push(
        `<pre><code${lang ? ` class="language-${escapeHtml(lang)}"` : ""}>${escapeHtml(
          buf.join("\n"),
        )}</code></pre>`,
      );
      continue;
    }

    // table
    if (/^\s*\|/.test(line) && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] ?? "")) {
      const header = line
        .split("|")
        .slice(1, -1)
        .map((c) => `<th>${inline(c.trim())}</th>`)
        .join("");
      i += 2;
      const rows: string[] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        const cells = lines[i]
          .split("|")
          .slice(1, -1)
          .map((c) => `<td>${inline(c.trim())}</td>`)
          .join("");
        rows.push(`<tr>${cells}</tr>`);
        i++;
      }
      html.push(`<table><thead><tr>${header}</tr></thead><tbody>${rows.join("")}</tbody></table>`);
      continue;
    }

    // headings
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      html.push(`<h${level}>${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // hr
    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
      html.push("<hr/>");
      i++;
      continue;
    }

    // blockquote
    if (/^\s*>/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      html.push(`<blockquote>${renderMarkdown(buf.join("\n"))}</blockquote>`);
      continue;
    }

    // lists
    if (/^\s*[-*]\s+/.test(line)) {
      flushList(false);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      flushList(true);
      continue;
    }

    // blank
    if (line.trim() === "") {
      i++;
      continue;
    }

    // paragraph
    const buf: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,6}\s|\s*[-*]\s|\s*\d+\.\s|\s*>|\s*```|\s*\|)/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    if (buf.length > 0) {
      html.push(`<p>${inline(buf.join(" "))}</p>`);
      continue;
    }

    // Fallback: a line that matched no branch (e.g. a stray `|` that isn't a
    // table, or a table whose separator line didn't match). Consume it so the
    // loop ALWAYS makes progress — otherwise the renderer hangs forever.
    html.push(`<p>${inline(line)}</p>`);
    i++;
  }

  return html.join("\n");
}
